/**
 * 에디터 관련 핸들러 함수들
 */

/**
 * 원본 코드의 ___ 위치를 찾아서 수정된 코드에서 채워진 값만 추출
 * @param {string} original - 원본 코드
 * @param {string} modified - 수정된 코드
 * @returns {string[]} 채워진 값들의 배열
 */
export const extractFilledValues = (original, modified) => {
    const originalLines = original.split('\n');
    const modifiedLines = modified.split('\n');
    const filledValues = [];

    originalLines.forEach((origLine, i) => {
        const modLine = modifiedLines[i] || "";
        if (!origLine.includes('___')) return;

        // 1. 특수 문자 이스케이프 (정규식 예약어 처리)
        // 2. ___ 패턴을 캡처 그룹 (.*?)으로 변환
        // 3. 문장 앞뒤 공백 유연하게 처리하기 위해 \s* 활용 가능
        const regexStr = origLine
            .replace(/[.*+?^${}()|[\]\\]/g, '\\$&') 
            .replace(/_{3,}/g, '(.*?)');

        const regex = new RegExp(`^${regexStr}$`);
        const match = modLine.match(regex);

        if (match) {
            // match[0]은 전체 문장이므로 제외, 각 캡처 그룹만 추출
            const extracted = match.slice(1)
                .map(val => val.trim())
                .filter(val => val !== "");

            if (extracted.length > 0) {
                filledValues.push(extracted.join(' '));
            }
        }
    });

    return filledValues;
};

/**
 * 라인 하이라이트 decoration 업데이트
 * @param {string} fileName - 파일 이름
 * @param {number[]} lineNumbers - 선택된 라인 번호 배열
 * @param {object} refs - 필요한 ref 객체들
 */
export const updateLineDecorations = (fileName, lineNumbers, refs) => {
    const { editorRef, decorationsRef } = refs;
    
    if (!editorRef.current) return;
    
    const editor = editorRef.current;
    const decorationNames = decorationsRef.current[fileName] || [];
    // 기존 decoration 제거
    if (decorationNames.length > 0) {
        editor.deltaDecorations(decorationNames, []);
    }   
    // 현재 파일의 전체 라인 수 가져오기
    const model = editor.getModel();
    const totalLines = model?.getLineCount();
    const selectedLinesSet = new Set(lineNumbers || []);
    
    // 모든 라인에 decoration 추가 (선택된 라인은 파란 체크, 선택되지 않은 라인은 회색 체크)
    const allDecorations = [];
    for (let lineNumber = 1; lineNumber <= totalLines; lineNumber++) {
        const isSelected = selectedLinesSet.has(lineNumber);
        allDecorations.push({
            range: {
                startLineNumber: lineNumber,
                startColumn: 1,
                endLineNumber: lineNumber,
                endColumn: 1
            },
            options: {
                isWholeLine: true,
                className: isSelected ? 'selected-line-highlight' : '',
                glyphMarginClassName: isSelected ? 'selected-line-glyph' : 'unselected-line-glyph'
            }
        });
    }
    
    const names = editor.deltaDecorations([], allDecorations);
    decorationsRef.current[fileName] = names;
};

/**
 * 에디터 마운트 핸들러
 * @param {object} editor - Monaco Editor 인스턴스
 * @param {object} monaco - Monaco 객체
 * @param {object} params - 필요한 상태 및 함수들
 */
export const handleEditorDidMount = (editor, monaco, params) => {
    const {
        editorRef,
        activeFileStep,
        activeFileNameRef,
        step,
        activeFileName,
        selectedLines,
        selectedProblem,
        updateLineDecorations,
        handleLineClick,
        decorationsRef
    } = params;

    editorRef.current = editor;
    
    // activeFileStep === 1일 때만 라인 선택 기능 활성화 (1단계 파일이거나 2단계에서 1단계 파일을 볼 때)
    if (activeFileStep === 1) {
        // 라인 번호 영역 클릭 이벤트
        // activeFileNameRef를 사용하여 항상 최신 활성 파일 이름을 참조
        editor.onMouseDown((e) => {
            // 라인 번호 영역(glyph margin 또는 line numbers) 클릭인지 확인
            if (e.target.type === monaco.editor.MouseTargetType.GUTTER_LINE_NUMBERS || 
                e.target.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) {
                const lineNumber = e.target.position?.lineNumber;
                if (lineNumber) {
                    // ref를 통해 현재 활성 파일 이름을 가져옴
                    const currentFileName = activeFileNameRef.current;
                    handleLineClick(currentFileName, lineNumber, params);
                }
            }
        });
        
        // 초기 마운트 시 현재 파일의 decoration 적용
        // 상태 업데이트 타이밍 문제를 해결하기 위해 약간의 지연 추가
        setTimeout(() => {
            if (!editorRef.current) return;
            
            // step === 1이면 selectedLines에서, step >= 2이면 selectedProblem.vulFiles에서 가져오기
            let lines = [];
            if (step === 1) {
                const fileData = Array.isArray(selectedLines)
                    ? selectedLines.find(item => item.fileName === activeFileName)
                    : null;
                lines = fileData ? fileData.lineNumbers : [];
            } else if (step >= 2) {
                const file = selectedProblem.vulFiles?.find(item => item.fileName === activeFileName);
                lines = file?.lineNums || [];
            }
            updateLineDecorations(activeFileName, lines, { editorRef, decorationsRef });
        }, 0);
    }
};

/**
 * 에디터 변경 핸들러
 * @param {string} value - 변경된 에디터 내용
 * @param {object} event - 변경 이벤트
 * @param {object} params - 필요한 상태 및 함수들
 */
export const handleEditorDidChange = (value, event, params) => {
    const {
        activeFileStep,
        activeFileName,
        originalCode,
        setFileContents,
        setModifiedCode,
        extractFilledValues
    } = params;

    if (activeFileStep === 2) {
        const originalCodeContent = originalCode.find(f => f.fileName === activeFileName)?.content || '';
        const modifiedCode = value || '';

        // 전체 수정된 내용 저장 (파일 전환 시 복원용)
        setFileContents(prev => ({
            ...prev,
            [activeFileName]: modifiedCode
        }));

        const filledValues = extractFilledValues(originalCodeContent, modifiedCode);

        setModifiedCode(prev => {
            const fileIndex = prev.findIndex(item => item.fileName === activeFileName);
            if (fileIndex !== -1) {
                // 기존 파일 업데이트
                const newModifiedCode = [...prev];
                newModifiedCode[fileIndex] = { fileName: activeFileName, keywords: filledValues };
                return newModifiedCode;
            } else if (filledValues.length === 0) {
                const newModifiedCode = [...prev];
                newModifiedCode[fileIndex] = { fileName: activeFileName, keywords: null };
                return newModifiedCode;
            } else {
                // 새 파일 추가
                return [...prev, { fileName: activeFileName, keywords: filledValues }];
            }
        });
    }
};
